import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth } from './firebase';
import { dbg, dbgErr } from './debug';

const AuthContext = createContext(null);

/**
 * After every Firebase Auth sign-in (email/password or Google), make sure the
 * RTDB `users/{uid}` profile exists — created with **0 credits** for new
 * users. Idempotent: calls /api/users/bootstrap which creates if missing,
 * bumps last_login otherwise. Sending a fresh ID token so backend `verify_id_
 * token` accepts us even if the client cached an older one.
 */
async function ensureProfile(firebaseUser, extra = {}) {
  try {
    const token = await firebaseUser.getIdToken(/* forceRefresh */ true);
    const body = {
      email: firebaseUser.email || '',
      full_name:
        extra.full_name
        ?? firebaseUser.displayName
        ?? (firebaseUser.email ? firebaseUser.email.split('@')[0] : ''),
    };
    const res = await fetch('/api/users/bootstrap', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      dbgErr('auth', `bootstrap failed (${res.status})`);
    } else {
      const data = await res.json().catch(() => ({}));
      dbg('auth', `bootstrap ok — created=${data.created}`);
    }
  } catch (e) {
    dbgErr('auth', 'bootstrap error', e);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbg('auth', 'subscribing to onAuthStateChanged');
    const unsub = onAuthStateChanged(auth, (u) => {
      dbg('auth', u ? 'signed in' : 'signed out', u ? { uid: u.uid, email: u.email } : null);
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login: async (email, password) => {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        // Ensure RTDB profile exists (a user who registered via the SPA
        // BEFORE bootstrap was wired up will not have one yet).
        await ensureProfile(cred.user);
        return cred.user;
      },
      register: async (email, password, fullName) => {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        if (fullName) {
          try {
            await updateProfile(cred.user, { displayName: fullName });
          } catch { /* non-fatal */ }
        }
        // Create RTDB profile with 0 credits via the bootstrap endpoint.
        await ensureProfile(cred.user, { full_name: fullName });
        return cred.user;
      },
      /**
       * Google sign-in via Firebase popup. Same post-auth hook as email/
       * password: ensure the RTDB profile exists (with 0 credits on first
       * sign-in). Sidesteps the forgot-password flow entirely for users
       * who'd rather authenticate with Google.
       */
      loginWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        // Ask Google for basic profile info + email scope explicitly.
        provider.addScope('email');
        provider.addScope('profile');
        const cred = await signInWithPopup(auth, provider);
        await ensureProfile(cred.user, { full_name: cred.user.displayName });
        return cred.user;
      },
      logout: () => signOut(auth),
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
